import { auth } from "@/firebaseConfig";
import { cn } from "@/lib/utils";
import { isElectron, isQashqoolMobile } from "@/scripts/environmentDetector";
import { getHostname } from "@/scripts/getHostname";
import { setHostname } from "@/store/slices/hostnameSlice";
import { signOut } from "firebase/auth";
import {
  ChartColumnIncreasing,
  House,
  LogOut,
  Menu,
  Monitor,
  Radio,
  ShoppingCart,
  User,
  Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink as Link, useLocation, useNavigate } from "react-router-dom";
import { endpoints } from "../../hooks/endpoints";
import usePWA from "../../hooks/usePWA";
import { getToken, isTeacher } from "../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  currentTeacherData,
  isPortalPending,
  isUserPending,
  setPortal,
  setUserProfileData,
  userData,
} from "../../store/slices/storeSlice";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, portalRefetch, userRefetch, portal, userProfileData } =
    useAppSelector((state) => state.store);
  const { hostname } = useAppSelector((state) => state.hostname);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAppInstalled } = usePWA();

  const handleLogout = () => {
    localStorage.removeItem("user");
    dispatch(userData(null));
    dispatch(setUserProfileData(null));
    dispatch(setHostname(""));
    signOut(auth);
    // navigate("/");
  };

  useEffect(() => {
    if (!user)
      fetch(endpoints.me, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        method: "GET",
      })
        .then((response) => response.json())
        .then((res) => {
          if (res.uid) {
            dispatch(userData(res));
          }
        })
        .catch((err) => {
          console.log(err);
          localStorage.removeItem("user");
          navigate("/");
        })
        .finally(() => dispatch(isUserPending(false)));
  }, []);

  useEffect(() => {
    if (!getHostname()) return;
    fetch(
      endpoints.getPortal +
        `?portal=${
          getHostname() !== "localhost"
            ? getHostname()
            : // : "localhost"
              "test-teacher.qashqool.com"
              // "azhar-academy.com"
          // "drtamerzakaria.com"
        }`,
      {
        method: "POST",
        body: JSON.stringify({
          portal:
            getHostname() !== "localhost" ? getHostname() : "azhar-academy.com",
        }),
      }
    )
      .then((response) => response.json())
      .then((result) => {
        dispatch(setPortal(result));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => dispatch(isPortalPending(false)));
  }, [portalRefetch]);

  useEffect(() => {
    if (user) {
      fetch(endpoints.getUser, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
          "cache-control": "no-cache",
        },
      })
        .then((response) => response.json())
        .then((result) => {
          dispatch(setUserProfileData(result));
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [userRefetch, user]);

  useEffect(() => {
    console.log("current portal after update", portal);

    if (portal) {
      try {
        // update teacher data
        dispatch(currentTeacherData(portal?.category));
        const logo = document.getElementById("logo-webp") as HTMLImageElement;
        logo.src = portal?.logoUrl;
        const favicon = document.getElementById(
          "teacher-favicon"
        ) as HTMLLinkElement;
        favicon.href = portal?.favicon;
        document.title = portal?.name;
      } catch (error) {
        console.error("Error fetching manifest:", error);
      }
    }
  }, [portal]);

  useEffect(() => {
    const notificationAudio = new Audio(
      "https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/notification.mp3"
    );
    let userinteraction = false;
    document.addEventListener("click", () => {
      if (userinteraction) return;
      userinteraction = true;
      notificationAudio.volume = 1;
    });
  }, []);

  const hideNavbar = location.pathname.includes("/live/");

  return (
    <>
      {hideNavbar ? null : (
        <header className="w-full h-16 bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50 shadow-sm">
          <div className="container mx-auto flex h-16 items-center justify-between px-4">
            <Link to="/" className="order-1 sm:order-[unset]">
              {hostname ? (
                <img
                  id="logo-webp"
                  className="h-13 rounded"
                  src=""
                  alt="Logo"
                />
              ) : (
                <img
                  className="h-13 rounded"
                  src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/qashqool-logo-dark.svg"
                  alt="Logo"
                />
              )}
            </Link>

            {user && hostname && (
              <nav className="hidden md:flex items-center gap-8 order-2">
                <NavLinks />
              </nav>
            )}

            {user && (
              <Link
                to="/"
                className="hidden md:flex bg-red-600 hover:bg-red-700 order-4 justify-around items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-normal transition-colors"
                onClick={() => handleLogout()}
              >
                <LogOut className="size-5 rotate-180" />
                خروج
              </Link>
            )}

            <div className="md:hidden flex items-center gap-2">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="size-8" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="p-4">
                  <div className="flex flex-col space-y-4 mt-8">
                    <NavLinks onClick={() => setOpen(false)} />
                    {user && (
                      <Link
                        to="/"
                        className="flex bg-red-600 hover:bg-red-700 justify-center items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-normal transition-colors"
                        onClick={() => {
                          handleLogout();
                          setOpen(false);
                        }}
                      >
                        <LogOut className="w-4 h-4 mr-2 rotate-180" />
                        خروج
                      </Link>
                    )}
                    {/* <a
                      href="/install"
                      className="text-sm font-medium bg-primary px-3 py-2 rounded-md text-white flex items-center justify-center hover:bg-secondary gap-2 transition-colors"
                    >
                      <MonitorPlay className="size-5" />
                      حمل تطبيق كشكول
                    </a> */}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>
      )}
    </>
  );
};

function NavLinks({ onClick }: { onClick?: () => void }) {
  const { user, userProfileData } = useAppSelector((state) => state.store);
  const { hostname } = useAppSelector((state) => state.hostname);
  const dispatch = useAppDispatch();
  return (
    <>
      <Link to="/" className={linkClasses()} onClick={onClick}>
        <House className="size-4" />
        الرئيسية
      </Link>
      {!!user && (
        <>
          {isTeacher() ? (
            <Link to="/dashboard" className={linkClasses()} onClick={onClick}>
              <ChartColumnIncreasing className="size-4" />
              لوحة التحكم
            </Link>
          ) : (
            <>
              <Link to="/account" className={linkClasses()} onClick={onClick}>
                <User className="size-4" />
                حسابي
              </Link>
              <Link to="/purchases" className={linkClasses()} onClick={onClick}>
                <ShoppingCart className="size-4" />
                مشترياتي
              </Link>
            </>
          )}
          <Link to="/live" className={linkClasses()} onClick={onClick}>
            <Radio className="size-4" />
            حصص لايف
          </Link>
          {(isElectron() || isQashqoolMobile()) && (
            <button
              className={`${linkClasses()} bg-transparent outline-none p-0 m-0 hover:cursor-pointer`}
              onClick={() => {
                dispatch(setPortal(null));
                dispatch(setHostname(""));
              }}
            >
              <Monitor className="size-4" />
              منصاتي
            </button>
          )}
        </>
      )}
      {["azhar-academy.com", "localhost"].includes(hostname) && (
        <Link
          to="/wallet"
          className="flex bg-primary hover:bg-primary/90 justify-around items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-normal transition-colors w-fit"
        >
          <Wallet className="size-5" /> :{" "}
          <span>{userProfileData?.wallet_credit ?? 0}</span> جنيه
        </Link>
      )}
    </>
  );
}

function linkClasses() {
  return cn(
    "text-base font-medium transition-colors hover:text-primary flex gap-2 items-center"
  );
}
export default Navbar;
